import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import * as signalR from "@microsoft/signalr";
import {
  LocalAudioTrack,
  LocalTrack,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  createLocalScreenTracks,
  createLocalVideoTrack,
} from "livekit-client";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import {
  BsDisplay,
  BsMic,
  BsMicMute,
  BsStopCircle,
  BsTelephone,
} from "react-icons/bs";
import { FaVideo, FaVideoSlash } from "react-icons/fa";
import "../css/VideoCall.css";

const VIDEO_CONTAINER_MIN_HEIGHT = 300;
const PLACEHOLDER_IMG_SIZE = 200;
const DEFAULT_USER_IMAGE = "/user.png";

const VIDEO_CONTAINER_STYLE: React.CSSProperties = {
  minHeight: VIDEO_CONTAINER_MIN_HEIGHT,
};

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

interface VideoCallProps {
  signalRHubUrl: string;
  liveKitTokenEndpoint: string;
}

function VideoCallInner({
  signalRHubUrl,
  liveKitTokenEndpoint,
}: VideoCallProps) {
  const { t } = useTranslation();
  const { targetObjectId } = useParams<{ targetObjectId?: string }>();
  const [objectId, setObjectId] = useState<string | null>(null);
  const [selectedObjectId] = useState<string>(targetObjectId || "");
  const [room, setRoom] = useState<Room | null>(null);
  const [connectionState, setConnectionState] =
    useState<string>("Disconnected");
  const [errorI18nKey, setErrorI18nKey] = useState<string | null>(null);
  const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const liveKitWsUrl = "wss://smbf-yu7ew340.livekit.cloud";
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraLoading, setIsCameraLoading] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const localAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  const localVideoTrackRef = useRef<LocalVideoTrack | null>(null);
  const screenVideoTrackRef = useRef<LocalVideoTrack | null>(null);
  const screenAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [callSeconds, setCallSeconds] = useState(0);

  // Call timer
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (room) {
      timer = setInterval(() => {
        setCallSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      setCallSeconds(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [room]);


  // Initial setup
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const oid = payload.oid;
        if (oid) {
          setObjectId(oid);
          startSignalR(oid, token);
        }
      } catch (err) {
        setErrorI18nKey("messages.video.errorDecodeToken");
      }
    }
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = async () => {
    if (room) {
      room.remoteParticipants.clear();
      const publications = room.localParticipant.getTrackPublications();
      const localTracks = publications
        .map((pub) => pub.track)
        .filter((track): track is LocalTrack => !!track && "sender" in track);
      room.localParticipant.unpublishTracks(localTracks);
      localTracks.forEach((track) => track.stop());
      await room.disconnect();
    }
    if (hubConnectionRef.current) {
      await hubConnectionRef.current.stop();
      hubConnectionRef.current = null;
    }
    if (videoContainerRef.current) {
      videoContainerRef.current.innerHTML = "";
    }
    setRoom(null);
    setIsCameraOn(false);
    setIsMuted(false);
  };

  const startSignalR = useCallback(async (objectId: string, token: string) => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(signalRHubUrl, { accessTokenFactory: () => token })
      .withAutomaticReconnect()
      .build();


    try {
      await connection.start();
      hubConnectionRef.current = connection;
      setConnectionState("Connected");
    } catch (err) {
      console.error("SignalR connection failed:", err);
      setErrorI18nKey("messages.video.errorSignalR");
    }
  }, [signalRHubUrl]);

  const startLiveKitCall = async (roomName: string = "defaultRoom") => {
    if (!objectId) return;
    setIsCameraLoading(true);
    try {
      const tokenRes = await fetch(liveKitTokenEndpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ roomName, identity: objectId, identityName: objectId }),
      });

      const { token } = await tokenRes.json();
      const newRoom = new Room();
      await newRoom.connect(liveKitWsUrl, token);
      setRoom(newRoom);

      const videoTrack = await createLocalVideoTrack();
      localVideoTrackRef.current = videoTrack;
      const audioTrack = await createLocalAudioTrack();
      localAudioTrackRef.current = audioTrack;

      await newRoom.localParticipant.publishTrack(videoTrack);
      await newRoom.localParticipant.publishTrack(audioTrack);

      attachTrack(videoTrack, objectId);
      setIsCameraLoading(false);

      newRoom.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        attachTrack(track, participant.identity);
      });

      newRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
        detachTrack(track);
      });

      newRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
        removeParticipantVideo(participant.identity);
      });

      for (const participant of Array.from(newRoom.remoteParticipants.values())) {
        participant.trackPublications.forEach((pub) => {
          const track = pub.track;
          if (track) attachTrack(track, participant.identity);
        });
      }

      if (hubConnectionRef.current && selectedObjectId) {
        try {
          await hubConnectionRef.current.invoke(
            "NotifyUserToJoinRoom",
            selectedObjectId,
            roomName
          );
        } catch (err) {
          console.error("Failed to notify user via SignalR:", err);
        }
      }
    } catch (err) {
      console.error("LiveKit connection error:", err);
      setErrorI18nKey("messages.video.errorLiveKit");
    }
  };

  const leaveLiveKitCall = async () => {
    if (!room) return;
    try {
      await cleanup();
      window.close();
    } catch (err) {
      console.error("Error leaving LiveKit room:", err);
      setErrorI18nKey("messages.video.errorLeave");
    }
  };

  const updateLayout = useCallback(() => {
    if (!videoContainerRef.current) return;

    const screenShare = document.getElementById("screen-share-container");
    const videos = Array.from(videoContainerRef.current.querySelectorAll(".video-tile"));

    // Screen share active
    if (screenShare) {
      videoContainerRef.current.className = "video-container screen-share-active";
      videos.forEach((v) => (v.className = "video-tile small-video"));
      return;
    }

    // No screen share → adjust based on count
    if (videos.length === 1) {
      videoContainerRef.current.className = "video-container single-video";
      videos[0].className = "video-tile full-video";
    } else {
      videoContainerRef.current.className = "video-container multi-video";
      videos.forEach((v) => (v.className = "video-tile grid-video"));
    }
  }, []);

  const attachTrack = useCallback(
    (track: Track, participantId: string) => {
      if (!videoContainerRef.current) return;
      const isScreenShare = track.source === "screen_share";

      if (isScreenShare) {
        let screenDiv = document.getElementById("screen-share-container");
        if (!screenDiv) {
          screenDiv = document.createElement("div");
          screenDiv.id = "screen-share-container";
          screenDiv.className = "screen-share";
          videoContainerRef.current.appendChild(screenDiv);
        }
        screenDiv.innerHTML = "";
        const element = track.attach();
        element.style.width = "100%";
        element.style.height = "100%";
        element.style.objectFit = "contain";
        screenDiv.appendChild(element);

        updateLayout();
        return;
      }

      let participantDiv = document.getElementById(`video-${participantId}`);
      if (!participantDiv) {
        participantDiv = document.createElement("div");
        participantDiv.id = `video-${participantId}`;
        participantDiv.className = "video-tile";
        videoContainerRef.current.appendChild(participantDiv);
      }
      participantDiv.innerHTML = "";
      if (track.kind === "video") {
        const element = track.attach();
        element.style.width = "100%";
        element.style.height = "100%";
        element.style.objectFit = "cover";
        participantDiv.appendChild(element);
      } else {
        const img = document.createElement("img");
        img.src = DEFAULT_USER_IMAGE;
        img.alt = t("messages.video.altParticipant");
        img.className = "rounded-circle";
        img.width = PLACEHOLDER_IMG_SIZE;
        img.height = PLACEHOLDER_IMG_SIZE;
        img.loading = "lazy";
        img.decoding = "async";
        img.style.width = `${PLACEHOLDER_IMG_SIZE}px`;
        img.style.height = `${PLACEHOLDER_IMG_SIZE}px`;
        img.style.objectFit = "cover";
        img.style.padding = "40px";
        participantDiv.appendChild(img);
      }

      updateLayout();
    },
    [updateLayout, t]
  );
  
  const detachTrack = (track: Track) => {
    track.detach().forEach((el) => el.remove());
    if (track.source === "screen_share") {
      const screenDiv = document.getElementById("screen-share-container");
      if (screenDiv) screenDiv.remove();
    }
    updateLayout();
  };
  

  const removeParticipantVideo = (participantId: string) => {
    const participantDiv = document.getElementById(`video-${participantId}`);
    if (participantDiv && videoContainerRef.current?.contains(participantDiv)) {
      videoContainerRef.current.removeChild(participantDiv);
    }
  };

  const toggleCamera = async () => {
    if (!room) return;
    if (isCameraOn) {
      if (localVideoTrackRef.current) {
        await room.localParticipant.unpublishTrack(localVideoTrackRef.current);
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current = null;
        attachTrack({ kind: "image" } as unknown as Track, room.localParticipant.identity);
        setIsCameraOn(false);
      }
    } else {
      const newVideoTrack = await createLocalVideoTrack();
      localVideoTrackRef.current = newVideoTrack;
      await room.localParticipant.publishTrack(newVideoTrack);
      attachTrack(newVideoTrack, room.localParticipant.identity);
      setIsCameraOn(true);
    }
  };

  useEffect(() => {
    if (connectionState === "Connected") {
      startLiveKitCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState]);

  const handleToggleMute = useCallback(() => {
    const track = localAudioTrackRef.current;
    if (!track) return;
    isMuted ? track.unmute() : track.mute();
    setIsMuted((prev) => !prev);
  }, [isMuted]);

  const handleStartScreenShare = useCallback(async () => {
    const [videoTrack, audioTrack] = await createLocalScreenTracks();
    if (!room) return;
    screenVideoTrackRef.current = videoTrack as LocalVideoTrack;
    await room.localParticipant.publishTrack(screenVideoTrackRef.current);
    attachTrack(screenVideoTrackRef.current, (objectId ?? "") + "-screen");
    if (audioTrack) {
      screenAudioTrackRef.current = audioTrack as LocalAudioTrack;
      await room.localParticipant.publishTrack(screenAudioTrackRef.current);
    }
    setIsScreenSharing(true);
  }, [room, objectId, attachTrack]);

  const handleStopScreenShare = useCallback(() => {
    if (!room) return;
    if (screenVideoTrackRef.current) {
      room.localParticipant.unpublishTrack(screenVideoTrackRef.current);
      screenVideoTrackRef.current.stop();
      removeParticipantVideo((objectId ?? "") + "-screen");
      screenVideoTrackRef.current = null;
    }
    if (screenAudioTrackRef.current) {
      room.localParticipant.unpublishTrack(screenAudioTrackRef.current);
      screenAudioTrackRef.current.stop();
      screenAudioTrackRef.current = null;
    }
    setIsScreenSharing(false);
  }, [room, objectId]);

  return (
    <div className="video-call-wrapper">
      <div
        ref={videoContainerRef}
        className="video-container"
        style={VIDEO_CONTAINER_STYLE}
      />

      {errorI18nKey && (
        <div className="alert alert-danger m-2" role="alert">
          {t(errorI18nKey)}
        </div>
      )}

      {isCameraLoading && (
        <div className="loading-overlay">
          <Spinner animation="border" role="status" variant="light" />
        </div>
      )}

      {!isCameraLoading && (
        <div className="call-timer">
          {t("messages.video.callTime")} {formatTime(callSeconds)}
        </div>
      )}

      <div className="controls-wrapper">
        <div className="controls-bar">
          <button
            className={`btn ${isCameraOn ? "btn-success" : "btn-danger"}`}
            onClick={toggleCamera}
            title={
              isCameraOn
                ? t("messages.video.cameraOff")
                : t("messages.video.cameraOn")
            }
          >
            {isCameraOn ? <FaVideo /> : <FaVideoSlash />}
          </button>
          <button
            className={`btn ${isMuted ? "btn-danger" : "btn-warning"}`}
            onClick={handleToggleMute}
            title={
              isMuted ? t("messages.video.unmute") : t("messages.video.mute")
            }
          >
            {isMuted ? <BsMicMute /> : <BsMic />}
          </button>
          {!isScreenSharing ? (
            <button
              className="btn btn-info"
              onClick={handleStartScreenShare}
              title={t("messages.video.startScreenShare")}
            >
              <BsDisplay />
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={handleStopScreenShare}
              title={t("messages.video.stopScreenShare")}
            >
              <BsStopCircle />
            </button>
          )}
          <button
            className="btn btn-danger"
            onClick={leaveLiveKitCall}
            title={t("messages.video.endCall")}
          >
            <BsTelephone />
          </button>
        </div>
      </div>
    </div>
  );
}

VideoCallInner.displayName = "VideoCall";

export default memo(VideoCallInner);
